import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Pause, Play } from 'lucide-react';

import { Button, Card, Slider } from '@llura/ui';

import { useCoachStore } from '../../../stores/coach.store';
import { useStrings } from '../../../hooks/useStrings';
import { useSafeExit } from '../../../hooks/useSafeExit';
import { getGuidedSession } from '../../../lib/coach-audio';

interface Level3AudioProps {
  onComplete: () => void;
}

function fmt(seconds: number): string {
  if (!isFinite(seconds)) return '00:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Real `<audio>`-driven guided session. Phase 1 ships silent placeholder
 * MP3s so timers tick against actual metadata. If the file is missing
 * (script hasn't been run), we transparently fall back to an interval
 * timer so the UX stays complete.
 *
 * The audio ref is registered with {@link useSafeExit} so the dezente
 * shield button pauses playback immediately on safe exit.
 */
export function Level3Audio({ onComplete }: Level3AudioProps) {
  const { name, gender, tone } = useCoachStore();
  const { strings, interp } = useStrings();
  const { registerAudio } = useSafeExit();

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fallbackIntervalRef = useRef<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  /** Real clip length once `<audio>` metadata loads — keeps the timer honest. */
  const [actualDuration, setActualDuration] = useState<number | null>(null);
  /** Flips when `<audio>` errors out (file missing or unsupported). */
  const [fallback, setFallback] = useState(false);

  const session = gender && tone ? getGuidedSession(gender, tone) : null;
  const totalSeconds =
    !fallback && actualDuration && isFinite(actualDuration) && actualDuration > 0
      ? actualDuration
      : session?.durationS ?? 240;

  // Register the audio element so safe-exit can stop it.
  useEffect(() => {
    if (!audioRef.current) return undefined;
    return registerAudio(audioRef.current);
  }, [registerAudio]);

  // Fallback interval kicks in only when `<audio>` failed to load.
  useEffect(() => {
    if (!fallback) return undefined;
    if (!playing) {
      if (fallbackIntervalRef.current !== null) {
        window.clearInterval(fallbackIntervalRef.current);
        fallbackIntervalRef.current = null;
      }
      return undefined;
    }
    fallbackIntervalRef.current = window.setInterval(() => {
      setPos((p) => {
        if (p >= totalSeconds) {
          setPlaying(false);
          return totalSeconds;
        }
        return p + 1;
      });
    }, 1000);
    return () => {
      if (fallbackIntervalRef.current !== null) {
        window.clearInterval(fallbackIntervalRef.current);
        fallbackIntervalRef.current = null;
      }
    };
  }, [fallback, playing, totalSeconds]);

  const togglePlay = () => {
    if (fallback) {
      setPlaying((p) => !p);
      return;
    }
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().catch(() => {
        setFallback(true);
        setPlaying(true);
      });
    } else {
      el.pause();
    }
  };

  const seek = (nextSeconds: number) => {
    setPos(nextSeconds);
    const el = audioRef.current;
    if (el && !fallback) {
      el.currentTime = nextSeconds;
    }
  };

  const pct = totalSeconds === 0 ? 0 : (pos / totalSeconds) * 100;

  return (
    <div className="flex flex-col items-center gap-6 pt-2">
      <div className="text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">{strings.levels.audio.label}</p>
        <h3 className="mt-1 font-serif text-2xl">{strings.levels.audio.title}</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {name
            ? interp(strings.levels.audio.sessionPrefix, { name })
            : strings.levels.audio.sessionPrefixFallback}
        </p>
      </div>

      <Card className="relative w-full max-w-sm overflow-hidden border-border bg-card p-0">
        <div className="relative aspect-square w-full overflow-hidden">
          <img
            alt=""
            src="https://images.unsplash.com/photo-1516575334481-f85287c2c82d?w=720&q=80&auto=format&fit=crop"
            className="h-full w-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/20 to-background" />
          <motion.div
            aria-hidden
            className="pointer-events-none absolute -inset-1 rounded-full"
            animate={playing ? { opacity: [0.25, 0.55, 0.25] } : { opacity: 0.15 }}
            transition={{ duration: 3.4, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              background:
                'radial-gradient(circle at 50% 80%, hsl(var(--primary) / 0.40), transparent 60%)',
            }}
          />
        </div>

        <div className="space-y-4 p-5">
          <Slider
            value={[pct]}
            max={100}
            step={0.1}
            onValueChange={(v) => seek((v[0] / 100) * totalSeconds)}
            aria-label={strings.levels.audio.seekAria}
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{fmt(pos)}</span>
            <span>{fmt(totalSeconds)}</span>
          </div>

          <div className="flex items-center justify-center">
            <Button
              size="icon"
              onClick={togglePlay}
              className="h-16 w-16 rounded-full bg-primary text-primary-foreground glow-primary"
              aria-label={playing ? strings.levels.audio.pauseAria : strings.levels.audio.playAria}
            >
              {playing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 translate-x-0.5" />}
            </Button>
          </div>
        </div>
      </Card>

      {session && (
        <audio
          ref={audioRef}
          src={session.url}
          preload="metadata"
          onLoadedMetadata={(e) => setActualDuration(e.currentTarget.duration)}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onTimeUpdate={(e) => setPos(e.currentTarget.currentTime)}
          onEnded={() => setPlaying(false)}
          onError={() => setFallback(true)}
        />
      )}

      <Button
        onClick={onComplete}
        size="lg"
        className="rounded-full bg-accent px-10 text-accent-foreground glow-accent"
      >
        {strings.levels.audio.completeCta}
      </Button>
    </div>
  );
}
